import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  defaultDropAnimationSideEffects,
  useDroppable,
  useSensor,
  useSensors,
  type DropAnimation,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { CheckCircle2, Combine, SplitSquareHorizontal } from "lucide-react";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  METRIC_TOOLTIP_ARROW_CLASS_NAME,
  METRIC_TOOLTIP_BODY_CLASS,
  metricTooltipShellClassName,
} from "@/components/ui/metric-tooltip";
import {
  Tooltip as ShadcnTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TableOutlineDeleteIconButton } from "@/components/ui/table-outline-delete-button";
import { cn } from "@/lib/utils";
import { exitPlacementPickDisplayLabel, isExitPlacementPick } from "../../../utils/exitPlacementPicks";
import { formatExitPlacementPickSlTpLine } from "../../../utils/exitPlacementPolicy";
import { humanizeSituationPickLabel } from "../../../utils/policyCatalogFieldLabels";
import type { PickQuickStats } from "../../../utils/reportPickQuickStats";
import type { PickImportDiagnostics } from "../../../utils/reportPickSlicePolicy";
import {
  getSimulatorLane,
  type ReportPick,
  type SimulatorPickLane,
  resolvePickRanSeparatelyCount,
  resolvePickUsedInCombinedCount,
} from "../../../utils/reportPickListStorage";
import type { SimulatorPickBundleCard } from "../../../utils/simulatorPickBundlesStorage";
import { statisticsSidebarSurfaceCardClassName } from "../Statistics/components/statisticsShadcnCardShell";
import { SimulatorPickQuickStats } from "./SimulatorPickQuickStats";

export type SimulatorPicksKanbanProps = {
  picks: ReportPick[];
  combinedBundles: SimulatorPickBundleCard[];
  selectedAtomicIds: Set<string>;
  onSelectedAtomicIdsChange: React.Dispatch<React.SetStateAction<Set<string>>>;
  linkedPickId: string | null;
  scopedReportId?: string | null;
  pickDiag: (p: ReportPick) => PickImportDiagnostics;
  onRemovePick: (p: ReportPick) => void;
  onRemoveBundle: (b: SimulatorPickBundleCard) => void;
  onMovePickLane: (p: ReportPick, lane: SimulatorPickLane) => void;
  quickStatsForPick?: (pick: ReportPick) => PickQuickStats | null;
  quickStatsForBundle?: (bundle: SimulatorPickBundleCard) => PickQuickStats | null;
  boardContainerRef?: React.Ref<HTMLDivElement>;
  clearSelectionButton: React.ReactNode | null;
};

type LaneConfig = {
  key: SimulatorPickLane;
  label: string;
  emptyLabel: string;
  tooltip: string;
  iconBoxClass: string;
  iconTextClass: string;
  iconBorderColor: string;
  Icon: typeof SplitSquareHorizontal;
};

type DragLane = "single" | "ran";
type LaneItems = Record<DragLane, string[]>;

const LANES: LaneConfig[] = [
  {
    key: "single",
    label: "Available Single Picks",
    emptyLabel: "No available single picks yet. Add picks to get started.",
    tooltip:
      "Queue for individual picks that have not been run separately yet. Combined-run usage is tracked on the card and does not move the pick here.",
    iconBoxClass: "border-solid border-[#38BBD1]/55",
    iconTextClass: "text-[#38BBD1]",
    iconBorderColor: "color-mix(in oklab, #38BBD1 55%, transparent)",
    Icon: SplitSquareHorizontal,
  },
  {
    key: "ran",
    label: "Executed single picks",
    emptyLabel: "No executed single picks yet — run picks separately to move them here.",
    tooltip:
      "Individual picks that have been run separately. Combined-run usage is shown on the card counters and does not by itself move a pick into this lane.",
    iconBoxClass: "border-solid border-[color:var(--tessera-pnl-positive)]/55",
    iconTextClass: "text-[color:var(--tessera-pnl-positive)]",
    iconBorderColor: "color-mix(in oklab, var(--tessera-pnl-positive) 55%, transparent)",
    Icon: CheckCircle2,
  },
  {
    key: "combined",
    label: "Executed combined picks",
    emptyLabel: "No combined runs yet. Create one by combining picks.",
    tooltip:
      "Combined-run cards created when you run selected picks together. Each card is one run with its own metrics; select one to highlight its leaf picks.",
    iconBoxClass: "border-solid border-[color:var(--tessera-pnl-positive)]/55",
    iconTextClass: "text-[color:var(--tessera-pnl-positive)]",
    iconBorderColor: "color-mix(in oklab, var(--tessera-pnl-positive) 55%, transparent)",
    Icon: Combine,
  },
];

const DRAG_LANE_KEYS: DragLane[] = ["single", "ran"];

/** Accent for Combined ran cards + leaf-pick “belongs to selected combined” highlight. */
const COMBINED_RAN_ACCENT = "#06E363";
const COMBINED_RAN_RING = "color-mix(in oklab, var(--tessera-pnl-positive) 35%, transparent)";
const COMBINED_RAN_BG = "color-mix(in oklab, var(--tessera-pnl-positive) 12%, transparent)";
const LINKED_PICK_ACCENT = "#38BBD1";
const DEFAULT_CARD_BORDER = "#d3d3d3";

const LANE_SHELL_CLASS = cn(
  "flex min-h-0 min-w-0 flex-1 flex-col rounded-[6px] border border-solid bg-muted/40 shadow-none dark:bg-sidebar/40"
);

const LANE_COUNT_BADGE_CLASS =
  "h-5 min-w-5 justify-center rounded-full border border-solid bg-muted/70 px-1.5 font-[family-name:var(--font-primary)] font-normal tabular-nums text-muted-foreground dark:bg-muted/40";

const ENTITY_CARD_CLASS = cn(
  "flex w-full min-w-0 flex-col gap-2.5 rounded-[6px] border border-solid bg-card p-3 text-left text-card-foreground shadow-none ring-0 transition-colors",
  "hover:border-[#38BBD1]/50",
  statisticsSidebarSurfaceCardClassName
);

function kanbanCardStrokeStyle(opts?: {
  borderColor?: string;
  backgroundColor?: string;
  boxShadow?: string;
}): React.CSSProperties {
  return {
    boxSizing: "border-box",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: opts?.borderColor ?? DEFAULT_CARD_BORDER,
    borderRadius: 6,
    boxShadow: opts?.boxShadow ?? "none",
    ...(opts?.backgroundColor ? { backgroundColor: opts.backgroundColor } : {}),
  };
}

const KANBAN_STROKE_STYLE: React.CSSProperties = kanbanCardStrokeStyle();

const DROP_ANIMATION: DropAnimation = {
  duration: 200,
  easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
  sideEffects: defaultDropAnimationSideEffects({
    styles: {
      active: { opacity: "0.5" },
    },
  }),
};

function laneDroppableId(lane: SimulatorPickLane): string {
  return `lane:${lane}`;
}

function parseLaneId(id: UniqueIdentifier | undefined | null): DragLane | null {
  const raw = String(id ?? "");
  if (raw === "lane:single" || raw === "lane:ran") {
    return raw.slice("lane:".length) as DragLane;
  }
  return null;
}

function buildLaneItems(picks: ReportPick[]): LaneItems {
  const out: LaneItems = { single: [], ran: [] };
  for (const pick of picks) {
    const lane = getSimulatorLane(pick);
    if (lane === "ran") out.ran.push(pick.id);
    else out.single.push(pick.id);
  }
  return out;
}

function findLaneForId(items: LaneItems, id: UniqueIdentifier): DragLane | null {
  const asLane = parseLaneId(id);
  if (asLane) return asLane;
  const key = String(id);
  for (const lane of DRAG_LANE_KEYS) {
    if (items[lane].includes(key)) return lane;
  }
  return null;
}

function pickDisplayTitle(p: ReportPick): string {
  if (isExitPlacementPick(p)) return exitPlacementPickDisplayLabel(p);
  return humanizeSituationPickLabel(p.label);
}

function pickMetaLine(
  p: ReportPick,
  d: PickImportDiagnostics,
  scopedReportId: string | null | undefined
): string {
  if (p.kind === "exit_placement") {
    const parts = ["Exit placement", pickDisplayTitle(p)];
    if (!scopedReportId && p.reportId) {
      parts.push(`report ${p.reportId.slice(0, 8)}…`);
    }
    parts.push(d.importable ? "rule ready" : "needs rule");
    return parts.join(" · ");
  }
  const parts: string[] = [p.kind];
  if (!scopedReportId && p.reportId) {
    parts.push(`report ${p.reportId.slice(0, 8)}…`);
  }
  if (p.sliceContext?.label) {
    parts.push(p.sliceContext.label);
  } else if (p.sliceId) {
    parts.push(`slice=${p.sliceId}`);
  }
  if (d.importable) {
    parts.push("convertible");
  } else if (d.unresolvedSliceDimensions.length > 0) {
    parts.push(`needs mapping (slice: ${d.unresolvedSliceDimensions.join(", ")})`);
  } else if (d.unresolvedContentKeys.length > 0) {
    parts.push(`needs mapping (content: ${d.unresolvedContentKeys.join(", ")})`);
  } else {
    parts.push("needs mapping");
  }
  return parts.join(" · ");
}

// ---------------------------------------------------------------------------
// Lane shell — droppable container
// ---------------------------------------------------------------------------

function PickLaneShell({
  lane,
  count,
  isOver,
  selectAllChecked,
  selectAllDisabled,
  onSelectAllChange,
  children,
}: {
  lane: LaneConfig;
  count: number;
  isOver: boolean;
  selectAllChecked: boolean | "indeterminate";
  selectAllDisabled?: boolean;
  onSelectAllChange: (checked: boolean) => void;
  children: React.ReactNode;
}): React.ReactElement {
  const Icon = lane.Icon;
  const { setNodeRef } = useDroppable({
    id: laneDroppableId(lane.key),
    data: { type: "lane", lane: lane.key },
  });

  return (
    <section
      ref={setNodeRef}
      data-simulator-picks-lane=""
      data-simulator-picks-lane-key={lane.key}
      className={cn(LANE_SHELL_CLASS, isOver && "ring-2 ring-[#38BBD1]/40")}
      style={KANBAN_STROKE_STYLE}
    >
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-border px-3 py-3">
        <div className="flex min-w-0 items-center gap-2">
          <span
            data-simulator-picks-lane-icon={lane.key}
            className={cn(
              "flex size-7 shrink-0 items-center justify-center rounded-[6px] border border-solid bg-card dark:bg-sidebar",
              lane.iconBoxClass
            )}
            style={{
              boxSizing: "border-box",
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: lane.iconBorderColor,
              borderRadius: 6,
            }}
          >
            <Icon className={cn("size-3.5", lane.iconTextClass)} aria-hidden />
          </span>
          <ShadcnTooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <h3
                className={cn(
                  "cursor-help truncate font-[family-name:var(--font-primary)] text-sm font-semibold",
                  lane.iconTextClass
                )}
              >
                {lane.label}
              </h3>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              sideOffset={4}
              arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
              className={cn(metricTooltipShellClassName("max-w-[280px]"), "z-[1200]")}
            >
              <p className={`text-left leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>{lane.tooltip}</p>
            </TooltipContent>
          </ShadcnTooltip>
          <Badge
            variant="outline"
            data-simulator-picks-lane-count=""
            className={LANE_COUNT_BADGE_CLASS}
            style={{
              boxSizing: "border-box",
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: "#d3d3d3",
            }}
          >
            {count}
          </Badge>
        </div>
        <div data-trade-log-row-checkbox="" className="flex shrink-0 items-center">
          <Checkbox
            data-trade-log-table-checkbox=""
            data-trade-log-select-all-header=""
            checked={selectAllChecked}
            disabled={selectAllDisabled || count === 0}
            onCheckedChange={(value) => onSelectAllChange(Boolean(value))}
            aria-label={`Select all in ${lane.label}`}
          />
        </div>
      </header>
      <div
        data-simulator-picks-lane-body=""
        className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-3 py-3"
        style={{ maxHeight: "min(50vh, 520px)", minHeight: "8rem" }}
      >
        {children}
      </div>
    </section>
  );
}

function LaneEmpty({ message }: { message: string }): React.ReactElement {
  return (
    <div className="flex flex-1 flex-col items-center justify-center rounded-[6px] border border-dashed border-border bg-card/60 px-4 py-8 text-center dark:bg-sidebar/30">
      <p className="font-[family-name:var(--font-primary)] text-sm text-muted-foreground">{message}</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sortable card slot
// ---------------------------------------------------------------------------

function KanbanSortablePick({
  id,
  isDragActive,
  children,
}: {
  id: string;
  isDragActive: boolean;
  children: React.ReactNode;
}): React.ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // When this card is the active drag source, make it transparent so only the
    // DragOverlay ghost is visible — no double-card effect.
    opacity: isDragging ? 0 : 1,
    cursor: isDragActive ? (isDragging ? "grabbing" : "grab") : "grab",
    zIndex: isDragging ? 10 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card body (pure display, no drag state)
// ---------------------------------------------------------------------------

function PickCardBody({
  pick,
  selected,
  linked,
  highlightedFromCombined,
  scopedReportId,
  diag,
  stats,
  onToggleSelected,
  onRemove,
}: {
  pick: ReportPick;
  selected: boolean;
  linked: boolean;
  /** True when this leaf pick is a constituent of a currently selected Combined ran card. */
  highlightedFromCombined?: boolean;
  scopedReportId?: string | null;
  diag: PickImportDiagnostics;
  stats: PickQuickStats | null | undefined;
  onToggleSelected: (checked: boolean) => void;
  onRemove: () => void;
}): React.ReactElement {
  const title = pickDisplayTitle(pick);
  const lane = getSimulatorLane(pick);
  // Executed singles can carry custom run names; long available titles also truncate.
  const showTitleTooltip = lane === "ran" || title.trim().length > 42;
  const usedInCombined = resolvePickUsedInCombinedCount(pick);
  const ranSeparately = resolvePickRanSeparatelyCount(pick);
  const slTpLine = isExitPlacementPick(pick) ? formatExitPlacementPickSlTpLine(pick) : null;

  return (
    <div
      data-simulator-picks-card=""
      data-simulator-picks-card-lane={lane}
      data-state={linked || highlightedFromCombined ? "active" : undefined}
      data-highlighted-from-combined={highlightedFromCombined ? "" : undefined}
      className={cn(ENTITY_CARD_CLASS, lane === "ran" && "opacity-95")}
      style={
        linked
          ? kanbanCardStrokeStyle({
              borderColor: LINKED_PICK_ACCENT,
              boxShadow: `0 0 0 1px color-mix(in oklab, ${LINKED_PICK_ACCENT} 40%, transparent)`,
            })
          : highlightedFromCombined
            ? kanbanCardStrokeStyle({
                borderColor: COMBINED_RAN_ACCENT,
                backgroundColor: COMBINED_RAN_BG,
                boxShadow: `0 0 0 1px ${COMBINED_RAN_RING}`,
              })
            : KANBAN_STROKE_STYLE
      }
    >
      <div className="flex items-start gap-2">
        <div
          data-trade-log-row-checkbox=""
          className="flex shrink-0 pt-0.5"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Checkbox
            data-trade-log-table-checkbox=""
            checked={selected}
            disabled={!diag.importable}
            onCheckedChange={(value) => onToggleSelected(Boolean(value))}
            aria-label={`Select ${title}`}
          />
        </div>
        <div className="min-w-0 flex-1">
          {showTitleTooltip ? (
            <ShadcnTooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <p
                  data-simulator-picks-title=""
                  className="cursor-help font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)] underline decoration-dotted underline-offset-2"
                >
                  {title}
                </p>
              </TooltipTrigger>
              <TooltipContent
                side="top"
                sideOffset={4}
                arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
                className={cn(metricTooltipShellClassName("max-w-[420px]"), "z-[1200]")}
              >
                <p className={`whitespace-pre-wrap text-left leading-snug ${METRIC_TOOLTIP_BODY_CLASS}`}>
                  {title}
                </p>
              </TooltipContent>
            </ShadcnTooltip>
          ) : (
            <p
              data-simulator-picks-title=""
              className="font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)]"
            >
              {title}
            </p>
          )}
          <p
            data-simulator-picks-meta=""
            className="mt-1 font-ibm-plex text-xs font-normal text-gray-500 dark:text-sidebar-muted-foreground"
          >
            {pickMetaLine(pick, diag, scopedReportId)}
          </p>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 font-ibm-plex text-xs font-normal text-gray-500 dark:text-sidebar-muted-foreground">
            <span>
              Used in combined{" "}
              <span className="font-semibold text-gray-600 dark:text-sidebar-muted-foreground">
                {usedInCombined}
              </span>
            </span>
            <span>
              Ran separately{" "}
              <span className="font-semibold text-gray-600 dark:text-sidebar-muted-foreground">
                {ranSeparately}
              </span>
            </span>
          </div>
          {slTpLine ? (
            <p
              data-simulator-picks-sl-tp=""
              className="mt-1 font-ibm-plex-mono text-xs font-normal text-gray-600 dark:text-sidebar-muted-foreground"
            >
              {slTpLine}
            </p>
          ) : isExitPlacementPick(pick) && !diag.importable ? (
            <p
              data-simulator-picks-sl-tp=""
              className="mt-1 font-ibm-plex-mono text-xs font-normal text-gray-400 dark:text-sidebar-muted-foreground"
            >
              SL — · TP —
            </p>
          ) : null}
          <SimulatorPickQuickStats stats={stats} />
        </div>
      </div>

      <div
        className="flex items-center justify-end gap-2 border-t border-border pt-2.5"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <TableOutlineDeleteIconButton
          marker="simulator"
          aria-label="Remove pick"
          onClick={onRemove}
        />
      </div>
    </div>
  );
}

function isGenericOverlapRunTitle(raw: string): boolean {
  return /^Overlap\s+(suggestion\s+)?#\d+(\s*·\s*)?$/i.test(raw.trim());
}

function parseOverlapSuggestionHeading(
  raw: string,
  policyLabels: string
): {
  policyTitle: string;
  overlapBadge: string | null;
} {
  const policy = policyLabels.trim();
  const withPolicy = raw.match(/^Overlap\s+#(\d+)\s*·\s*(.+)$/i);
  if (withPolicy) {
    return {
      policyTitle: withPolicy[2]!.trim() || policy || raw,
      overlapBadge: `Overlap #${withPolicy[1]}`,
    };
  }
  const legacy = raw.match(/^Overlap\s+suggestion\s+#(\d+)\s*$/i);
  if (legacy) {
    return {
      policyTitle: policy || raw,
      overlapBadge: `Overlap #${legacy[1]}`,
    };
  }
  const legacyPrefixed = raw.match(/^Overlap\s+suggestion\s+#(\d+)\s*·\s*(.*)$/i);
  if (legacyPrefixed) {
    return {
      policyTitle: legacyPrefixed[2]!.trim() || policy || raw,
      overlapBadge: `Overlap #${legacyPrefixed[1]}`,
    };
  }
  const overlapOnly = raw.match(/^Overlap\s+#(\d+)\s*$/i);
  if (overlapOnly) {
    return {
      policyTitle: policy || raw,
      overlapBadge: `Overlap #${overlapOnly[1]}`,
    };
  }
  return { policyTitle: raw, overlapBadge: null };
}

function BundleCardBody({
  bundle,
  selected,
  stats,
  policyLabelsText,
  policyTooltipText,
  onToggleSelected,
  onRemove,
}: {
  bundle: SimulatorPickBundleCard;
  selected: boolean;
  stats: PickQuickStats | null | undefined;
  /** Human-readable pick labels joined with " + " (preferred card title). */
  policyLabelsText: string;
  /** Full combine expression for the tooltip (e.g. `(a OR b)`). */
  policyTooltipText: string;
  onToggleSelected: (checked: boolean) => void;
  onRemove: () => void;
}): React.ReactElement {
  const storedTitle = bundle.title?.trim() || "";
  const policyLabels = (bundle.policySummary?.trim() || policyLabelsText).trim();
  const policyExpression = policyTooltipText.trim();
  const policyForTitle = policyLabels || policyExpression;
  const heading = storedTitle || policyForTitle || "Untitled run";
  const { policyTitle, overlapBadge } = parseOverlapSuggestionHeading(heading, policyForTitle);
  // Never keep a bare "Overlap suggestion #N" title when we can show real policies.
  const displayTitle =
    (policyTitle.trim() && !isGenericOverlapRunTitle(policyTitle) ? policyTitle.trim() : null) ||
    policyForTitle ||
    heading;
  const tooltipBody = policyExpression || policyLabels || displayTitle;

  return (
    <div
      data-simulator-picks-card=""
      data-simulator-picks-card-lane="combined"
      data-state={selected ? "active" : undefined}
      className={cn(ENTITY_CARD_CLASS, "border-solid")}
      style={
        selected
          ? kanbanCardStrokeStyle({
              borderColor: COMBINED_RAN_ACCENT,
              backgroundColor: COMBINED_RAN_BG,
              boxShadow: `0 0 0 1px ${COMBINED_RAN_RING}`,
            })
          : KANBAN_STROKE_STYLE
      }
    >
      <div className="flex items-start gap-2">
        <div
          data-trade-log-row-checkbox=""
          className="flex shrink-0 pt-0.5"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Checkbox
            data-trade-log-table-checkbox=""
            checked={selected}
            onCheckedChange={(value) => onToggleSelected(Boolean(value))}
            aria-label={`Select ${displayTitle}`}
          />
        </div>
        <div className="min-w-0 flex-1">
          <ShadcnTooltip delayDuration={300}>
            <TooltipTrigger asChild>
              <p
                data-simulator-picks-title=""
                className="cursor-help line-clamp-2 whitespace-normal break-words font-[family-name:var(--font-primary)] text-sm font-medium text-[color:var(--brand-tropical-surf)] underline decoration-dotted underline-offset-2"
              >
                {displayTitle}
              </p>
            </TooltipTrigger>
            <TooltipContent
              side="top"
              sideOffset={4}
              arrowClassName={METRIC_TOOLTIP_ARROW_CLASS_NAME}
              className={cn(metricTooltipShellClassName("max-w-[420px]"), "z-[1200]")}
            >
              {overlapBadge ? (
                <p className="font-[family-name:var(--font-primary)] text-xs font-medium text-[color:var(--brand-tropical-surf)]">
                  {overlapBadge}
                </p>
              ) : null}
              <p
                className={cn(
                  "whitespace-pre-wrap text-left leading-snug",
                  overlapBadge && "mt-1",
                  METRIC_TOOLTIP_BODY_CLASS
                )}
              >
                {tooltipBody}
              </p>
            </TooltipContent>
          </ShadcnTooltip>
          <div
            data-simulator-picks-meta=""
            className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 font-ibm-plex text-xs font-normal text-gray-500 dark:text-sidebar-muted-foreground"
          >
            <span>
              Runs{" "}
              <span className="font-semibold text-gray-600 dark:text-sidebar-muted-foreground">
                1{bundle.status === "running" ? " · running" : ""}
              </span>
            </span>
            <span>
              Used in combined{" "}
              <span className="font-semibold text-gray-600 dark:text-sidebar-muted-foreground">
                {bundle.used_as_child_count}
              </span>
            </span>
          </div>
          {bundle.status === "running" ? (
            <div className="mt-1 font-ibm-plex text-xs font-normal text-gray-500 dark:text-sidebar-muted-foreground">
              Waiting for simulation…
            </div>
          ) : stats ? (
            <SimulatorPickQuickStats stats={stats} />
          ) : null}
        </div>
      </div>

      <div
        className="flex items-center justify-end gap-2 border-t border-border pt-2.5"
        onPointerDown={(e) => e.stopPropagation()}
      >
        <TableOutlineDeleteIconButton
          marker="simulator"
          aria-label="Remove combined pick"
          onClick={onRemove}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main kanban component
// ---------------------------------------------------------------------------

export function SimulatorPicksKanban(props: SimulatorPicksKanbanProps): React.ReactElement {
  const [laneItems, setLaneItems] = useState<LaneItems>(() => buildLaneItems(props.picks));
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overLane, setOverLane] = useState<DragLane | null>(null);

  // Sync when picks change externally (e.g. after a run marks them).
  useEffect(() => {
    setLaneItems(buildLaneItems(props.picks));
  }, [props.picks]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const pickById = useMemo(() => {
    const map = new Map<string, ReportPick>();
    for (const pick of props.picks) map.set(pick.id, pick);
    return map;
  }, [props.picks]);

  const bundleById = useMemo(() => {
    const map = new Map<string, SimulatorPickBundleCard>();
    for (const b of props.combinedBundles) map.set(b.id, b);
    return map;
  }, [props.combinedBundles]);

  const activePick = activeId ? (pickById.get(activeId) ?? null) : null;

  const resolveBundleChildLabels = useCallback(
    (bundleId: string, visited: Set<string> = new Set()): string[] => {
      if (visited.has(bundleId)) return ["…"];
      visited.add(bundleId);

      const bundle = bundleById.get(bundleId);
      if (!bundle) return [bundleId];

      const labels: string[] = [];
      for (const childId of bundle.children) {
        const pick = pickById.get(childId);
        if (pick) {
          labels.push(pickDisplayTitle(pick));
          continue;
        }
        if (bundleById.has(childId)) {
          labels.push(...resolveBundleChildLabels(childId, visited));
          continue;
        }
        labels.push(childId);
      }
      return labels.filter((l) => l.trim().length > 0);
    },
    [bundleById, pickById]
  );

  const resolveBundlePolicyLabels = useCallback(
    (bundleId: string): string => resolveBundleChildLabels(bundleId).join(" + "),
    [resolveBundleChildLabels]
  );

  const resolveBundlePolicyTooltip = useCallback(
    (bundleId: string, visited: Set<string> = new Set()): string => {
      if (visited.has(bundleId)) return "…";
      visited.add(bundleId);

      const bundle = bundleById.get(bundleId);
      if (!bundle) return bundleId;

      const op = bundle.combineOp === "and" ? "AND" : "OR";
      const childExprs: string[] = [];
      for (const childId of bundle.children) {
        const pick = pickById.get(childId);
        if (pick) {
          childExprs.push(pickDisplayTitle(pick));
          continue;
        }
        if (bundleById.has(childId)) {
          childExprs.push(resolveBundlePolicyTooltip(childId, visited));
          continue;
        }
        childExprs.push(childId);
      }

      if (childExprs.length === 0) return `${bundleId}`;
      if (childExprs.length === 1) return childExprs[0]!;
      return `(${childExprs.join(` ${op} `)})`;
    },
    [bundleById, pickById]
  );

  /** Leaf pick ids that belong to any currently selected Combined ran card (recursive). */
  const highlightedLeafPickIdsFromSelectedBundles = useMemo(() => {
    const out = new Set<string>();
    const collectLeafs = (cardId: string, visited: Set<string>) => {
      if (visited.has(cardId)) return;
      visited.add(cardId);
      if (pickById.has(cardId)) {
        out.add(cardId);
        return;
      }
      const bundle = bundleById.get(cardId);
      if (!bundle) return;
      for (const childId of bundle.children) collectLeafs(childId, visited);
    };

    props.selectedAtomicIds.forEach((id) => {
      if (!bundleById.has(id)) return;
      collectLeafs(id, new Set());
    });
    return out;
  }, [bundleById, pickById, props.selectedAtomicIds]);

  const selectedCount = props.selectedAtomicIds.size;

  const laneSelectState = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return { checked: false as boolean | "indeterminate", disabled: true };
      const selectedInLane = ids.filter((id) => props.selectedAtomicIds.has(id)).length;
      if (selectedInLane === 0) return { checked: false as boolean | "indeterminate", disabled: false };
      if (selectedInLane === ids.length) return { checked: true as boolean | "indeterminate", disabled: false };
      return { checked: "indeterminate" as boolean | "indeterminate", disabled: false };
    },
    [props.selectedAtomicIds]
  );

  const toggleLaneSelection = useCallback(
    (ids: string[], checked: boolean) => {
      props.onSelectedAtomicIdsChange((prev) => {
        const next = new Set(prev);
        if (checked) {
          for (const id of ids) next.add(id);
        } else {
          for (const id of ids) next.delete(id);
        }
        return next;
      });
    },
    [props.onSelectedAtomicIdsChange]
  );

  // -------------------------------------------------------------------------
  // Drag handlers
  // -------------------------------------------------------------------------

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(String(active.id));
  };

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    if (!over) { setOverLane(null); return; }

    const activeLane = findLaneForId(laneItems, active.id);
    const targetLane = findLaneForId(laneItems, over.id);
    setOverLane(targetLane);

    if (!activeLane || !targetLane || activeLane === targetLane) return;

    setLaneItems((prev) => {
      const srcList = prev[activeLane];
      const dstList = prev[targetLane];
      const srcIndex = srcList.indexOf(String(active.id));
      if (srcIndex < 0) return prev;

      const overIsLane = Boolean(parseLaneId(over.id));
      const overIndex = overIsLane ? dstList.length : dstList.indexOf(String(over.id));
      const insertAt = overIndex < 0 ? dstList.length : overIndex;

      return {
        ...prev,
        [activeLane]: srcList.filter((id) => id !== String(active.id)),
        [targetLane]: [
          ...dstList.slice(0, insertAt),
          String(active.id),
          ...dstList.slice(insertAt),
        ],
      };
    });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const id = String(active.id);
    setActiveId(null);
    setOverLane(null);

    const pick = pickById.get(id);

    if (!over) {
      // Dropped outside — revert.
      setLaneItems(buildLaneItems(props.picks));
      return;
    }

    if (!pick) {
      setLaneItems(buildLaneItems(props.picks));
      return;
    }

    const persistedLane = getSimulatorLane(pick);
    const currentLane = findLaneForId(laneItems, id);

    const persistedDragLane: DragLane = persistedLane === "ran" ? "ran" : "single";

    if (currentLane && currentLane !== persistedDragLane) {
      // Cross-lane drop — persist the new lane.
      props.onMovePickLane(pick, currentLane);
      return;
    }

    // Same lane — within-lane reorder (local only, no persistence needed).
    if (currentLane) {
      const ids = laneItems[currentLane];
      const oldIndex = ids.indexOf(id);
      const newIndex = ids.indexOf(String(over.id));
      if (oldIndex >= 0 && newIndex >= 0 && oldIndex !== newIndex) {
        setLaneItems((prev) => ({
          ...prev,
          [currentLane]: arrayMove(prev[currentLane], oldIndex, newIndex),
        }));
      }
    }
  };

  const handleDragCancel = () => {
    setActiveId(null);
    setOverLane(null);
    setLaneItems(buildLaneItems(props.picks));
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <TooltipProvider delayDuration={300}>
    <div
      ref={props.boardContainerRef}
      data-simulator-picks-kanban=""
      className="flex w-full min-w-0 flex-col gap-3"
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        autoScroll={false}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* Three-column board — always grid-cols-3 */}
        <div className="grid w-full grid-cols-3 gap-3">
          {LANES.map((lane) => {
            const isCombinedLane = lane.key === "combined";
            if (isCombinedLane) {
              const combinedIds = props.combinedBundles.map((b) => b.id);
              const selectState = laneSelectState(combinedIds);
              return (
                <PickLaneShell
                  key={lane.key}
                  lane={lane}
                  count={props.combinedBundles.length}
                  isOver={false}
                  selectAllChecked={selectState.checked}
                  selectAllDisabled={selectState.disabled}
                  onSelectAllChange={(checked) => toggleLaneSelection(combinedIds, checked)}
                >
                  {props.combinedBundles.length === 0 ? (
                    <LaneEmpty message={lane.emptyLabel} />
                  ) : (
                    <div className="flex flex-col gap-3" data-simulator-picks-combined-cards="">
                      {props.combinedBundles.map((bundle) => (
                        <BundleCardBody
                          key={bundle.id}
                          bundle={bundle}
                          selected={props.selectedAtomicIds.has(bundle.id)}
                          stats={props.quickStatsForBundle?.(bundle)}
                          policyLabelsText={resolveBundlePolicyLabels(bundle.id)}
                          policyTooltipText={resolveBundlePolicyTooltip(bundle.id)}
                          onToggleSelected={(checked) => {
                            props.onSelectedAtomicIdsChange((prev) => {
                              const next = new Set(prev);
                              if (checked) next.add(bundle.id);
                              else next.delete(bundle.id);
                              return next;
                            });
                          }}
                          onRemove={() => props.onRemoveBundle(bundle)}
                        />
                      ))}
                    </div>
                  )}
                </PickLaneShell>
              );
            }

            const dragLane = lane.key as "single" | "ran";
            const ids = laneItems[dragLane];
            const selectState = laneSelectState(ids);
            return (
              <PickLaneShell
                key={lane.key}
                lane={lane}
                count={ids.length}
                isOver={overLane === lane.key && activeId != null}
                selectAllChecked={selectState.checked}
                selectAllDisabled={selectState.disabled}
                onSelectAllChange={(checked) => toggleLaneSelection(ids, checked)}
              >
                <SortableContext items={ids} strategy={verticalListSortingStrategy}>
                  {ids.length === 0 ? (
                    <LaneEmpty message={lane.emptyLabel} />
                  ) : (
                    ids.map((id) => {
                      const pick = pickById.get(id);
                      if (!pick) return null;
                      const diag = props.pickDiag(pick);
                      return (
                        <KanbanSortablePick key={id} id={id} isDragActive={activeId != null}>
                          <PickCardBody
                            pick={pick}
                            selected={props.selectedAtomicIds.has(pick.id)}
                            linked={props.linkedPickId === pick.id}
                            highlightedFromCombined={highlightedLeafPickIdsFromSelectedBundles.has(
                              pick.id
                            )}
                            scopedReportId={props.scopedReportId}
                            diag={diag}
                            stats={props.quickStatsForPick?.(pick)}
                            onToggleSelected={(checked) => {
                              props.onSelectedAtomicIdsChange((prev) => {
                                const next = new Set(prev);
                                if (checked) next.add(pick.id);
                                else next.delete(pick.id);
                                return next;
                              });
                            }}
                            onRemove={() => props.onRemovePick(pick)}
                          />
                        </KanbanSortablePick>
                      );
                    })
                  )}
                </SortableContext>
              </PickLaneShell>
            );
          })}
        </div>

        {/* Floating drag ghost — clean scale, no tilt */}
        <DragOverlay dropAnimation={DROP_ANIMATION}>
          {activePick ? (
            <div
              style={{
                boxShadow: "0 12px 32px rgba(0,0,0,0.22)",
                borderRadius: 6,
                transform: "scale(1.02)",
                cursor: "grabbing",
                pointerEvents: "none",
              }}
            >
              <PickCardBody
                pick={activePick}
                selected={props.selectedAtomicIds.has(activePick.id)}
                linked={props.linkedPickId === activePick.id}
                scopedReportId={props.scopedReportId}
                diag={props.pickDiag(activePick)}
                stats={props.quickStatsForPick?.(activePick)}
                onToggleSelected={() => undefined}
                onRemove={() => undefined}
              />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {/* Footer count */}
      <div className="flex items-center gap-2 py-1">
        <span className="flex-1 font-[family-name:var(--font-primary)] text-sm text-muted-foreground">
          {selectedCount} card(s) selected.
        </span>
        {props.clearSelectionButton}
      </div>
    </div>
    </TooltipProvider>
  );
}
